import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, Crown, X, Sparkles } from "lucide-react";

const PERKS = [
  "No banner or video ads — ever",
  "Uninterrupted full-length film playback",
  "4K / HDR quality where available",
  "Cancel anytime",
];

const PLANS = [
  { id: "monthly", label: "Monthly", price: "$3.99", per: "/ month", note: "Billed monthly" },
  { id: "yearly", label: "Yearly", price: "$43.09", per: "/ year", note: "Save 10% — 2 months free feel" },
];

const Premium = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      await refreshUser();
      toast("Subscription canceled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCanceling(false);
    }
  };

  const trialActive = user && user.trial_active;
  const isPaid = user && user.is_paid;

  return (
    <div className="min-h-screen bg-[#030303] pt-24 pb-20 px-4 flex items-start justify-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_50%_20%,rgba(0,240,255,0.14),transparent_45%)]" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#00F0FF]"><Crown size={16} /> Power2000 Premium</span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-white mt-3 text-glow">Ad-Free Streaming</h1>
          <p className="mt-3 inline-flex items-center gap-2 text-sm text-white/70"><Sparkles size={15} className="text-[#00F0FF]" /> Starts with a 3-day free trial — cancel anytime</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8">
          {/* Plan selector */}
          <div className="grid grid-cols-2 gap-3 mb-6" data-testid="plan-selector">
            {PLANS.map((p) => {
              const active = plan === p.id;
              return (
                <button key={p.id} type="button" onClick={() => setPlan(p.id)} data-testid={`plan-${p.id}`}
                  className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-white/10 bg-white/5 hover:border-white/30"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white">{p.label}</span>
                    {p.id === "yearly" && <span className="text-[9px] font-mono-ui tracking-widest text-black bg-[#00F0FF] rounded px-1.5 py-0.5">-10%</span>}
                  </div>
                  <div className="mt-2 flex items-end gap-1">
                    <span className="font-heading text-2xl font-extrabold text-white">{p.price}</span>
                    <span className="text-white/50 text-xs mb-0.5">{p.per}</span>
                  </div>
                  <p className="text-[11px] text-white/40 mt-1">{p.note}</p>
                </button>
              );
            })}
          </div>

          <ul className="space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-white/80">
                <span className="w-5 h-5 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-[#00F0FF]" />
                </span>
                {p}
              </li>
            ))}
          </ul>

          {isPaid ? (
            <div className="mt-8">
              <div className="flex items-center gap-2 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 px-4 py-3 text-sm text-[#00E676]" data-testid="premium-active">
                <Check size={18} /> You're Premium — ads are off.
              </div>
              <button onClick={cancel} disabled={canceling} data-testid="cancel-sub-btn" className="mt-4 w-full rounded-full bg-white/10 border border-white/20 text-white font-medium py-3 hover:bg-white/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {canceling ? <Loader2 size={18} className="animate-spin" /> : <X size={18} />} Cancel subscription
              </button>
            </div>
          ) : (
            <>
              {trialActive && (
                <div className="mt-6 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 px-4 py-3 text-sm text-[#00F0FF]" data-testid="trial-banner">
                  Your free trial is active — subscribe now to keep ads off after it ends.
                </div>
              )}
              <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn" className="mt-6 w-full rounded-full bg-[#00F0FF] text-black font-semibold py-3.5 hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-60 glow-cyan">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} />} Start 3-day free trial
              </button>
              <p className="mt-3 text-center text-xs text-white/40">Then {plan === "yearly" ? "$43.09/year" : "$3.99/month"} · cancel anytime · Test card 4242 4242 4242 4242</p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Premium;
